import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch } from '@/lib/api';

export type InjuryWithPlayer = {
  id: string;
  playerId: string;
  injuryType: string;
  severity: string | null;
  injuryDate: string;
  gameContext: string | null;
  doctor: string | null;
  projectedReturnDate: string | null;
  returnToPlayDate: string | null;
  status: 'active' | 'archived';
  firstGameBackFlagged: number | null;
  countdownAlertSent: number | null;
  notes: string | null;
  createdAt: number;
  updatedAt: number;
  createdBy: string | null;
  updatedBy: string | null;
  player: {
    firstName: string;
    lastName: string;
    displayName: string | null;
    currentTeam: string | null;
    currentLeague: string | null;
  } | null;
};

export type InjuryBuckets = {
  overdue: InjuryWithPlayer[];
  this_week: InjuryWithPlayer[];
  next_30: InjuryWithPlayer[];
  no_eta: InjuryWithPlayer[];
};

export type InjuriesResponse = {
  items: InjuryWithPlayer[];
  total: number;
  buckets?: InjuryBuckets;
};

export function useActiveInjuries() {
  return useQuery<InjuriesResponse>({
    queryKey: ['injuries', { status: 'active', bucket: true }],
    queryFn: () => apiGet<InjuriesResponse>('/api/injuries?status=active&bucket=1'),
  });
}

export function useArchivedInjuries() {
  return useQuery<InjuriesResponse>({
    queryKey: ['injuries', { status: 'archived' }],
    queryFn: () => apiGet<InjuriesResponse>('/api/injuries?status=archived'),
  });
}

export function usePlayerInjuries(playerId: string | undefined) {
  return useQuery<InjuriesResponse>({
    queryKey: ['injuries', { playerId }],
    enabled: Boolean(playerId),
    queryFn: () =>
      apiGet<InjuriesResponse>(`/api/injuries?playerId=${playerId}&status=active`),
  });
}

export type InjuryCreateInput = {
  playerId: string;
  injuryType: string;
  severity?: string | null;
  injuryDate: string;
  gameContext?: string | null;
  doctor?: string | null;
  projectedReturnDate?: string | null;
  notes?: string | null;
  status?: 'active' | 'archived';
};

export function useCreateInjury() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InjuryCreateInput) =>
      apiPost<{ injury: InjuryWithPlayer }>('/api/injuries', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['injuries'] });
      qc.invalidateQueries({ queryKey: ['player'] });
      qc.invalidateQueries({ queryKey: ['players'] });
    },
  });
}

export function useArchiveInjury() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; returnToPlayDate: string; notes?: string | null }) =>
      apiPost<{ injury: InjuryWithPlayer }>(`/api/injuries/${input.id}/archive`, {
        returnToPlayDate: input.returnToPlayDate,
        notes: input.notes ?? null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['injuries'] });
      qc.invalidateQueries({ queryKey: ['player'] });
      qc.invalidateQueries({ queryKey: ['players'] });
    },
  });
}

export function useUpdateInjury() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; patch: Partial<InjuryCreateInput> }) =>
      apiPatch<{ injury: InjuryWithPlayer }>(`/api/injuries/${input.id}`, input.patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['injuries'] });
      qc.invalidateQueries({ queryKey: ['player'] });
    },
  });
}
